import React, { createContext, ReactNode, useContext, useReducer } from 'react';

import { GlobalContext } from '../../../../../store';
import { IEventEnrollment } from '../../../../../store/admin/eventEnrollments/store';
import { IAdminFlight } from '../../../../../store/admin/eventFlights/store';
import { IEventDetailedRound, IRoundGroup } from '../../../../../store/admin/eventRounds/store';

import { isMobileResolution } from '../../../../../contrib/utils';
import { IObjectById } from '../../../../../contrib/Utils/getObjectByIdFromArray';
import { filterGroup } from '../Utils/filterGroup';
import { IOnPlayerAdd, onPlayerAdd } from './BoundActionCreators/onPlayerAdd';
import {
  IOnSelectPlayerToAdd,
  onSelectPlayerToAdd
} from './BoundActionCreators/onSelectPlayerToAdd';
import {
  IOnSelectPlayerToSwap,
  onSelectPlayerToSwap
} from './BoundActionCreators/onSelectPlayerToSwap';
import { IOnSelectSeat, onSelectSeat } from './BoundActionCreators/onSelectSeat';
import { ISetEnrollments, setEnrollments } from './BoundActionCreators/setEnrollments';
import { useRouteInfoState } from '../../../../../components/RouteInfo/Context/RouteInfo.context';
import { IOnPlayerSwap, onPlayerSwap } from './BoundActionCreators/onPlayerSwap';
import { getShotgunSpots, IGetShotgunSpots } from './BoundActionCreators/getShotgunSpots';
import { disableShotgunSpot, IDisableShotgunSpot } from './BoundActionCreators/disableShotgunSpot';

export interface IOpenGroups {
  [groupId: number]: boolean;
}

export interface IShotgunSpot {
  startingHole: number;
  startingPosition: string;
}

type IActions =
  | {
      type: 'setEnrollments';
      value: IObjectById<IEventEnrollment>;
    }
  | {
      type: 'filterGroups';
      value: { round: IEventDetailedRound };
    }
  | {
      type: 'setShotgunSpots';
      value: IShotgunSpot[];
    }
  | {
      type: 'set';
      value:
        | { attribute: 'flights'; value: { [flightId: number]: IAdminFlight } }
        | { attribute: 'dialogActions'; value: IDialogActions }
        | { attribute: 'selectedTeams'; value: number[] }
        | { attribute: 'searchValue' | 'dialogText' | 'dialogTitle'; value: string }
        | { attribute: 'modalOpen' | 'dialogOpen' | 'addTeamActive'; value: boolean }
        | { attribute: 'openGroups'; value: IOpenGroups }
        | {
            attribute:
              | 'selectedGroup'
              | 'selectedPlayer'
              | 'selectedSeat'
              | 'groupIdToAdd'
              | 'selectedSwap'
              | 'toRemoveId';
            value: number;
          };
    };

interface IDialogActions {
  toAdd?: number;
  toRemoveId?: number;
  toSwap1?: number;
  toSwap2?: number;
  clear?: boolean;
  autoFill?: boolean;
}

interface ITeeSheetCreatorState {
  addTeamActive: boolean;
  dialogActions: IDialogActions;
  dialogOpen: boolean;
  dialogText: string;
  dialogTitle: string;
  enrollments: IObjectById<IEventEnrollment>;
  filteredGroups: IRoundGroup[];
  flights: { [flightId: number]: IAdminFlight };
  groupIdToAdd: number;
  isMobile: boolean;
  modalOpen: boolean;
  openGroups: IOpenGroups;
  searchValue: string;
  selectedGroup: number;
  selectedPlayer: number;
  selectedSeat: number;
  selectedSwap: number;
  selectedTeams: number[];
  shotgunSpots: IShotgunSpot[];
}

const initialState: ITeeSheetCreatorState = {
  addTeamActive: false,
  dialogActions: {},
  dialogOpen: false,
  dialogText: '',
  dialogTitle: '',
  enrollments: {},
  filteredGroups: [],
  flights: {},
  groupIdToAdd: -1,
  isMobile: isMobileResolution(),
  modalOpen: false,
  openGroups: {},
  searchValue: '',
  selectedGroup: 0,
  selectedPlayer: 0,
  selectedSeat: 0,
  selectedSwap: 0,
  selectedTeams: [],
  shotgunSpots: []
};

interface IDispatch {
  dispatch: (action: IActions) => void;
  actions: IBoundActionCreators;
}

const TeeSheetCreatorStateContext = createContext<ITeeSheetCreatorState | undefined>(undefined);
const TeeSheetCreatorDispatchContext = createContext<IDispatch | undefined>(undefined);

const TeeSheetCreatorReducer = (
  state: ITeeSheetCreatorState,
  action: IActions
): ITeeSheetCreatorState => {
  switch (action.type) {
    case 'setEnrollments': {
      return {
        ...state,
        enrollments: action.value
      };
    }
    case 'set': {
      return {
        ...state,
        [action.value.attribute]: action.value.value
      };
    }
    case 'setShotgunSpots': {
      return {
        ...state,
        shotgunSpots: action.value
      };
    }
    case 'filterGroups': {
      const { searchValue } = state;
      const {
        value: { round }
      } = action;

      const currentGroups = round && round.groups ? round.groups : [];

      const filtered = currentGroups.filter(g => filterGroup(round, g, searchValue));

      const filteredGroups = filtered.length ? filtered : currentGroups;

      return {
        ...state,
        filteredGroups
      };
    }
  }
};

interface TeeSheetProviderProps {
  children: ReactNode;
}

interface IBoundActionCreators {
  getShotgunSpots: IGetShotgunSpots;
  disableShotgunSpot: IDisableShotgunSpot;
  onPlayerAdd: IOnPlayerAdd;
  onPlayerSwap: IOnPlayerSwap;
  onSelectPlayerToAdd: IOnSelectPlayerToAdd;
  onSelectPlayerToSwap: IOnSelectPlayerToSwap;
  onSelectSeat: IOnSelectSeat;
  setEnrollments: ISetEnrollments;
}

export type IStateDispatchActions<T> = (
  state: ITeeSheetCreatorState,
  dispatch: (action: IActions) => void
) => T;

const TeeSheetCreatorProvider = ({ children }: TeeSheetProviderProps) => {
  const [state, dispatch] = useReducer(TeeSheetCreatorReducer, initialState as never);

  const {
    actions: {
      adminEventRounds: { addPlayerToGroup, swapPlayers }
    }
  } = useContext(GlobalContext);

  const { clubId, eventId, roundId } = useRouteInfoState();

  const onPlayerAddAugmented = onPlayerAdd(state, dispatch)({
    addPlayerToGroup,
    clubId,
    eventId,
    roundId
  });

  const onSelectPlayerToSwapAugmented = onSelectPlayerToSwap(state, dispatch)({
    clubId,
    eventId,
    roundId,
    swapPlayers
  });

  const actions: IBoundActionCreators = {
    disableShotgunSpot: disableShotgunSpot(state, dispatch),
    getShotgunSpots: getShotgunSpots(state, dispatch),
    onPlayerAdd: onPlayerAddAugmented,
    onPlayerSwap: onPlayerSwap(state, dispatch)({ onSelectPlayerToSwapAugmented }),
    onSelectPlayerToAdd: onSelectPlayerToAdd(state, dispatch)(onPlayerAddAugmented),
    onSelectPlayerToSwap: onSelectPlayerToSwapAugmented,
    onSelectSeat: onSelectSeat(state, dispatch),
    setEnrollments: setEnrollments(state, dispatch)
  };

  return (
    <TeeSheetCreatorStateContext.Provider value={state}>
      <TeeSheetCreatorDispatchContext.Provider value={{ dispatch, actions }}>
        {children}
      </TeeSheetCreatorDispatchContext.Provider>
    </TeeSheetCreatorStateContext.Provider>
  );
};

const useTeeSheetCreatorState = () => {
  const context = React.useContext(TeeSheetCreatorStateContext);

  if (context === undefined) {
    throw new Error('useTeeSheetCreatorState must be used within a TeeSheetCreatorProvider');
  }

  return context;
};

const useTeeSheetCreatorDispatch = () => {
  const context = React.useContext(TeeSheetCreatorDispatchContext);

  if (context === undefined) {
    throw new Error('useTeeSheetCreatorDispatch must be used within a TeeSheetCreatorProvider');
  }

  return context;
};

export { TeeSheetCreatorProvider, useTeeSheetCreatorState, useTeeSheetCreatorDispatch };
